import { create } from 'zustand';
import { decodeJwt, type JwtClaims } from '@/lib/jwt';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/lib/constants';

interface AuthState {
  accessToken: string | null;
  user: JwtClaims | null;
  isAuthenticated: boolean;
  isInitialized: boolean;

  setTokens: (accessToken: string, refreshToken: string) => void;
  setAccessToken: (accessToken: string) => void;
  logout: () => void;
  initialize: () => void;
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
}

/**
 * Hydrate auth state from localStorage so that a page reload can send
 * the (possibly-expired) access token to the refresh endpoint.
 */
function hydrateFromStorage(): Pick<AuthState, 'accessToken' | 'user' | 'isAuthenticated'> {
  const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!storedAccessToken) {
    return { accessToken: null, user: null, isAuthenticated: false };
  }

  const user = decodeJwt(storedAccessToken);
  if (!user) {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return { accessToken: null, user: null, isAuthenticated: false };
  }

  // We have a decodable token — mark as authenticated so the UI doesn't
  // flash to login while the background refresh is in-flight.
  return { accessToken: storedAccessToken, user, isAuthenticated: true };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...hydrateFromStorage(),
  isInitialized: false,

  setTokens: (accessToken: string, refreshToken: string) => {
    const user = decodeJwt(accessToken);
    if (!user) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      set({ accessToken: null, user: null, isAuthenticated: false });
      return;
    }
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    set({ accessToken, user, isAuthenticated: true });
  },

  setAccessToken: (accessToken: string) => {
    const user = decodeJwt(accessToken);
    if (!user) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      set({ accessToken: null, user: null, isAuthenticated: false });
      return;
    }
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    set({ accessToken, user, isAuthenticated: true });
  },

  logout: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    set({ accessToken: null, user: null, isAuthenticated: false });
  },

  initialize: () => {
    set({ isInitialized: true });
  },

  getAccessToken: () => get().accessToken,

  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
}));
